import { Flex, Input } from '@pega/cosmos-react-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useLocalTranslation from '../../hooks/useLocalTranslation';
import { appState } from '../../interfaces/common';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../redux/actions';
import { updateUIToggle } from '../SocialMedia/Chat/ChatApis';
import { colorPickerStyle } from '../SocialMedia/Chat/Tabs/StylingTab';
import useContrastChecker from '../../hooks/useContrastChecker';
import ContrastWarning from './ContrastWarning';

interface ColorPickerProps {
  labelText: string;
  avatarType: string;
  colorType?: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ labelText, avatarType, colorType }) => {
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const navigate = useNavigate();
  const {
    chatInfo: {
      botBgColor = '',
      botFontColor = '',
      csrBgColor = '',
      csrFontColor = '',
      userBgColor = '',
      userFontColor = '',
      connectionId,
    },
    sessionId,
  } = useSelector((state: appState) => {
    return {
      chatInfo: state.chatInfo,
      sessionId: state.sessionId,
    };
  });

  const contrastChecker = useContrastChecker();

  const [csrContrastWarning, setCsrContrastWarning] = useState('');
  const [botContrastWarning, setBotContrastWarning] = useState('');
  const [userContrastWarning, setUserContrastWarning] = useState('');

  useEffect(() => {
    setBotContrastWarning(contrastChecker(botFontColor, botBgColor));
    setCsrContrastWarning(contrastChecker(csrFontColor, csrBgColor));
    setUserContrastWarning(contrastChecker(userBgColor, userFontColor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botBgColor, botFontColor, csrBgColor, csrFontColor, userBgColor, userFontColor]);

  const setColorKeyValue = (typeOfPicker: string) => {
    if (typeOfPicker === 'botFontColor') {
      return { value: botFontColor, key: 'botFontColor', type: 'Bot_Font_Update' };
    } else if (typeOfPicker === 'botBgColor') {
      return { value: botBgColor, key: 'botBgColor', type: 'Bot_Bg_Update' };
    } else if (typeOfPicker === 'csrBgColor') {
      return { value: csrBgColor, key: 'csrBgColor', type: 'Csr_Bg_Update' };
    } else if (typeOfPicker === 'csrFontColor') {
      return { value: csrFontColor, key: 'csrFontColor', type: 'Csr_Font_Update' };
    } else if (typeOfPicker === 'userBgColor') {
      return { value: userBgColor, key: 'userBgColor', type: 'User_Bg_Update' };
    } else return { value: userFontColor, key: 'userFontColor', type: 'User_Font_Update' };
  };

  const pickerType = avatarType === 'botAvatar' ? 'bot' : avatarType === 'userAvatar' ? 'user' : 'csr';
  let typeOfPicker = `${pickerType}${colorType}`;
  const colorConfig = setColorKeyValue(typeOfPicker);
  const [color, setColor] = useState('');
  useEffect(() => {
    setColor(colorConfig.value);
  }, [colorConfig.key, colorConfig.value]);

  const updateToggleConfig = async (togglePath: string, payload: any, bannerParam: string) => {
    dispatch(updateLoaderDetails(true));
    let json = await updateUIToggle(togglePath, sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate(`${bannerParam}_update_success`), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate(`${bannerParam}_update_failed`), 'urgent'));
    }
  };

  const handleColorChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const data = e.target.value;
    const { key, type } = colorConfig;
    setColor(data);
    await updateToggleConfig(
      'update_font_bg_color',
      {
        [key]: data,
        type,
      },
      `${avatarType}_${colorType}`,
    );
  };

  return (
    <>
      <Flex
        container={{
          direction: 'row',
          itemGap: 1,
          alignItems: 'end',
        }}
      >
        <Flex item={{ grow: 1 }}>
          <Input label={labelText} type="text" readOnly={true} name="color" value={color} />
        </Flex>
        <Input autoComplete="off" type="color" style={colorPickerStyle} value={color} onChange={handleColorChange} />
      </Flex>
      {typeOfPicker === 'userBgColor' && userContrastWarning && (
        <ContrastWarning warningMessage={userContrastWarning} />
      )}
      {typeOfPicker === 'botBgColor' && botContrastWarning && <ContrastWarning warningMessage={botContrastWarning} />}
      {typeOfPicker === 'csrBgColor' && csrContrastWarning && <ContrastWarning warningMessage={csrContrastWarning} />}
    </>
  );
};

export default ColorPicker;
